import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { TransferState } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Brand } from '../../shared/interfaces/brand';
import { Observable, Subject, merge, of } from 'rxjs';
import { ShopService } from '../../shared/api/shop.service';
import { OnlineProduct } from '../../shared/interfaces/product';
import { Category } from '../../shared/interfaces/category';
import { BlockHeaderGroup } from '../../shared/interfaces/block-header-group';
import { catchError, switchMap, takeUntil, tap } from 'rxjs/operators';
import { RootService, SITE_DESCRIPTION } from 'src/app/shared/services/root.service';
import { Post } from 'src/app/shared/interfaces/post';
import { BlogService } from 'src/app/shared/api/blog.service'; 
import { DEFAULT_BLOCK_TITLES, DEFAULT_TAB_NAMES, HomeBlock } from 'src/app/shared/api/home-shelf.service';
import { BRAND, SITE_ORIGIN } from 'src/app/shared/brand';
import { SitePagesService } from 'src/app/shared/api/site-pages.service';
import { SiteLanguage, SitePageView, SiteSectionItem } from 'src/app/shared/interfaces/site-page';
import { SiteLanguageService } from 'src/app/shared/services/site-language.service';
import { ShopFrontService } from 'src/app/shared/api/shop-front.service';
import { MODULE, SiteModulesService } from 'src/app/shared/services/site-modules.service';
import { carried } from 'src/app/modules/shop/services/carried';
import { CultureService } from 'src/app/shared/culture/culture.service';

/// The home page's own words, the owner's (Account -> Site -> Pages): the hero (the page's title and description - the
/// slide keeps its own lettering) and how it works - each a SitePage fragment, in the page's language where it has
/// one. The four promises under the slide are the theme's (block-features); the way in for sellers is the footer's.
const FRAGMENTS = ['home.hero', 'home.how'];

/// The first section of a fragment with a list - the one named `name` if there is one.
function listOf(page: SitePageView, name: RegExp = null): SiteSectionItem[] {
    const sections = (page.sections || []).filter(section => section.items && section.items.length);
    const named = name ? sections.filter(section => name.test(section.heading || ''))[0] : null;
    return (named || sections[0] || { items: [] }).items;
}

interface ProductsCarouselGroup extends BlockHeaderGroup {
    /// The bucket it draws from (HomeShelves), by which the owner names its tab.
    shelf: string;
    products$: Observable<OnlineProduct[]>;
}

interface ProductsCarouselData {
    abort$: Subject<void>;
    loading: boolean;
    products: OnlineProduct[];
    groups: ProductsCarouselGroup[];
}

@Component({
    selector: 'app-home',
    templateUrl: './page-home-one.component.html',
    styleUrls: ['./page-home-one.component.scss']
})
export class PageHomeOneComponent implements OnInit, OnDestroy {
    /// The brand, for the template (shared/brand.ts).
    readonly brand = BRAND;

    destroy$: Subject<void> = new Subject<void>(); 
    brands$: Observable<Brand[]>;
    popularCategories$: Observable<Category[]>; 
    columnTopRated$: Observable<OnlineProduct[]>; 
    columnLatestProducts: any[] = []; 
    posts$:Observable<Post[]>;; 
    featuredProducts: ProductsCarouselData;
    latestProducts: ProductsCarouselData; 

    /// The page's language (CultureService: /bn, or the language the visitor chose): the owner's words and the posts in it.
    lang: SiteLanguage = 'en';
    /// The address's language: /bn is the Bangla home page's own address, for the canonical link and og:url.
    private urlLang: SiteLanguage = 'en';
    /// The owner's words for the blocks, once read (on the server render, so they are in the page); null, the blocks'
    /// own.
    how: { heading: string, steps: string[] } = null;

    /// Shops to follow (ShopFront/ToFollow): empty until a shop qualifies, and the shelf is hidden while it is.
    shopsToFollow: any[] = [];
    /// The shelf's "All shops" link: only while the shops directory is switched on (the owner's Modules page).
    readonly shopsDirectory$: Observable<boolean>;

    /// The blocks' titles: the owner's, from the Home page settings, once read - on the server render, so the page is
    /// drawn with them - and these until then.
    titles: { [block: string]: string } = {
        'fresh-finds': this.culture.t('home-fresh-finds-title', 'Fresh Finds, Trending Styles – Start Exploring'),
        'spotlight': this.culture.t('home-spotlight-title', 'Discover Top-Quality Car & Bike Accessories for Every Drive'),
        'premium': this.culture.t('home-premium-title', 'Premium And Luxury Collections'),
    };

    constructor(
        private shop: ShopService,
        private root: RootService,
        private blogS:BlogService,
        route: ActivatedRoute,
        private pages: SitePagesService,
        private languages: SiteLanguageService,
        private shopFront: ShopFrontService,
        private transfer: TransferState,
        siteModules: SiteModulesService,
        @Inject(PLATFORM_ID) private platformId: any,
        private culture: CultureService
    )
    {
        this.shopsDirectory$ = siteModules.on$(MODULE.directory);
        this.urlLang = route.snapshot.data.lang === 'bn' ? 'bn' : 'en';
        this.lang = this.culture.lang === 'bn' ? 'bn' : 'en';

        // The tagline (setmeta adds the brand), as index.html has it - until the hero's own title arrives.
        this.root.setmeta(this.culture.t('home-tagline', 'Shop from Verified Sellers Across Bangladesh'), {
            og_description: SITE_DESCRIPTION,
            og_image: SITE_ORIGIN + '/assets/images/logos/primehat-share.png',
            og_type:"website",
            og_url: SITE_ORIGIN + (this.urlLang === 'bn' ? '/bn' : '')
        } );

        this.root.insertHomeSchema();
    }

    ngOnInit(): void
    {
        // The posts in the page's language; a Bangla home page with no Bangla post yet shows the English ones.
        this.posts$ = this.blogS.getBlogList('', '', this.lang).pipe(
            switchMap(posts => posts.length || this.lang === 'en' ? of(posts) : this.blogS.getBlogList('', '', 'en')));
        // The site's own address is the one (www answers with a redirect to it).
        this.root.createLinkForCanonicalURL(SITE_ORIGIN + (this.urlLang === 'bn' ? '/bn' : '/'));
        this.pages.fragments(FRAGMENTS, this.lang).pipe(takeUntil(this.destroy$)).subscribe(fragments => this.applyFragments(fragments));
       // this.brands$ = this.shop.getBrandsForPage("home", 10); 
        //this.popularCategories$ = this.shop.getPopularCategories();    
        // Every block and tab is a bucket the owner fills on the Home page settings (HomeShelves on the server): each page
        // load picks its cards at random from it, and the bucket's own rule fills what it cannot - so no two show the same
        // list. They asked for selectors the server could not fill: "toprated" and "saver" both fell through to the
        // newest products, and this block showed the Latest tab's list a second time.
        this.shop.getHomeShelf('spotlight', 7).pipe(takeUntil(this.destroy$)).subscribe(res =>
        {
            this.columnLatestProducts = res;
        });

        this.featuredProducts = {
            abort$: new Subject<void>(),
            loading: false,
            products: [],
            groups: [
                {
                    name: this.culture.t('latest', 'Latest'),
                    shelf: 'latest',
                    current: true,
                    products$: this.shop.getHomeShelf('latest', 7),
                },
                {
                    name: this.culture.t('super-saver', 'Super Saver'),
                    shelf: 'super-saver',
                    current: false,
                    products$: this.shop.getHomeShelf('super-saver', 7),
                },
                {
                    name: this.culture.t('best-deals', 'Best Deals'),
                    shelf: 'best-deals',
                    current: false,
                    products$: this.shop.getHomeShelf('best-deals', 7),
                }
            ],
        };
        this.groupChange(this.featuredProducts, this.featuredProducts.groups[0]);

        // The tab marked is the one loaded first: Mega Offers used to be marked over Luxury Deals' products.
        this.latestProducts = {
            abort$: new Subject<void>(),
            loading: false,
            products: [],
            groups: [
                {
                    name: this.culture.t('luxury-deals', 'Luxury Deals'),
                    shelf: 'luxury-deals',
                    current: true,
                    products$: this.shop.getHomeShelf('luxury-deals', 12)
                },
                {
                    name: this.culture.t('mega-offers', 'Mega Offers'),
                    shelf: 'mega-offers',
                    current: false,
                    products$: this.shop.getHomeShelf('mega-offers', 12)
                }
            ],
        };
        this.groupChange(this.latestProducts, this.latestProducts.groups[0]);

        // After the carousels exist: in the browser the carried answer arrives at once.
        this.shop.getHomeBlocks().pipe(takeUntil(this.destroy$)).subscribe(blocks => this.applyBlocks(blocks));

        // Asked on the server render and carried in the page - an empty answer too - so the browser neither asks again
        // nor moves the blocks below when it would come in. A failure is no shelf.
        carried(this.transfer, this.platformId, 'shops-to-follow', () => this.shopFront.toFollow()).pipe(
            catchError(() => of([] as any[])),
            takeUntil(this.destroy$),
        ).subscribe(shops => this.shopsToFollow = shops || []);
    }

    ngOnDestroy(): void {
        this.root.setHreflang([]);
        this.destroy$.next();
        this.destroy$.complete();
    }

    /// The owner's words into the blocks, and the hero's title and description as the page's.
    private applyFragments(fragments: { [key: string]: SitePageView }): void {
        const hero = fragments['home.hero'];
        if (hero) {
            // The Bangla home page is its own page only once its hero is Bangla; until then it is the English one.
            const home = this.urlLang === 'bn' && hero.language === 'bn' ? '/bn' : '/';
            if (hero.title) {
                this.root.setmeta(hero.title, {
                    og_description: hero.metaDescription || SITE_DESCRIPTION,
                    og_image: SITE_ORIGIN + '/assets/images/logos/primehat-share.png',
                    og_type: 'website',
                    og_url: SITE_ORIGIN + (home === '/' ? '' : home),
                }, true);
            }
            this.root.createLinkForCanonicalURL(SITE_ORIGIN + home);

            // The home page's versions are the hero's: / and /bn.
            const versions = (hero.translations || []).map(t => ({ language: t.language, path: t.language === 'bn' ? '/bn' : '/' }));
            this.root.setHreflang(versions);
            this.languages.setTranslations(versions);
        }

        const how = fragments['home.how'];
        if (how) {
            this.how = { heading: how.h1, steps: listOf(how).map(item => item.html) };
        }
    }

    /// The owner's titles and tab names onto the blocks drawn with the defaults. A title or name the owner left as the
    /// default English keeps the default's label, so it reads in the page's language; one the owner wrote is shown as written.
    private applyBlocks(blocks: HomeBlock[]): void {
        (blocks || []).forEach(block => {
            if (block.Title && block.Title !== DEFAULT_BLOCK_TITLES[block.Key]) { this.titles[block.Key] = block.Title; }
            (block.Tabs || []).forEach(tab => {
                [this.featuredProducts, this.latestProducts].forEach(carousel => carousel.groups
                    .filter(group => group.shelf === tab.Shelf && !!tab.Name && tab.Name !== DEFAULT_TAB_NAMES[tab.Shelf])
                    .forEach(group => group.name = tab.Name));
            });
        });
    }

    groupChange(carousel: ProductsCarouselData, group: BlockHeaderGroup): void {
        carousel.loading = true;
        carousel.abort$.next();

        (group as ProductsCarouselGroup).products$.pipe(
            tap(() => carousel.loading = false),
            takeUntil(merge(this.destroy$, carousel.abort$)),
        ).subscribe(x =>{ carousel.products = x;});
    }
}
